import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import signupstyles from "../styles/Signup/Buyersignup.module.css";
import { FcGoogle } from "react-icons/fc";
import { HiOutlineBuildingStorefront } from "react-icons/hi2";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useGoogleLogin } from "@react-oauth/google";
import { useDispatch, useSelector } from "react-redux";
import { signupThunk, googleSignupThunk } from "../redux/slices/authThunk";
import CustomAlert from "../components/alert/CustomAlert";
import { clearAuthMessages } from "../redux/slices/authSlice";

function Signup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { signupLoading, signupError, signupSuccessMessage } = useSelector(
    (state) => state.auth,
  );

  const queryParams = new URLSearchParams(location.search);

  const roleFromUrl = queryParams.get("role");

  const [role, setRole] = useState(() => {
    if (roleFromUrl === "seller") return "seller";
    if (roleFromUrl === "transporter") return "transporter";
    return "buyer";
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [alertData, setAlertData] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
  });

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    location: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (roleFromUrl === "seller") {
      setRole("seller");
    } else if (roleFromUrl === "transporter") {
      setRole("transporter");
    } else {
      setRole("buyer");
    }
  }, [roleFromUrl]);

  useEffect(() => {
    if (signupSuccessMessage) {
      setAlertData({
        show: true,
        type: "success",
        title: "Signup Successful",
        message: signupSuccessMessage,
      });
    }
  }, [signupSuccessMessage]);

  useEffect(() => {
    if (signupError) {
      setAlertData({
        show: true,
        type: "error",
        title: "Signup Failed",
        message: signupError,
      });
    }
  }, [signupError]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleGoogleSignup = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const response = await fetch(
          `https://www.googleapis.com/oauth2/v3/userinfo?access_token=${tokenResponse.access_token}`,
        );

        const userData = await response.json();

        dispatch(
          googleSignupThunk({
            fullName: userData.name,
            email: userData.email,
            profileImage: userData.picture,
            role,
          }),
        );
      } catch (error) {
        setAlertData({
          show: true,
          type: "error",
          title: "Google Signup Failed",
          message: "Something went wrong while signing up with Google.",
        });
      }
    },

    onError: () => {
      setAlertData({
        show: true,
        type: "error",
        title: "Google Signup Failed",
        message: "Unable to continue with Google signup.",
      });
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName || !formData.email || !formData.password) {
      setAlertData({
        show: true,
        type: "warning",
        title: "Required Fields",
        message: "Please fill all required fields before continuing.",
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setAlertData({
        show: true,
        type: "error",
        title: "Password Mismatch",
        message: "Password and confirm password do not match.",
      });
      return;
    }

    dispatch(
      signupThunk({
        ...formData,
        role,
      }),
    );

    setFormData({
      fullName: "",
      email: "",
      location: "",
      password: "",
      confirmPassword: "",
    });
  };

  return (
    <>
      {alertData.show && (
        <CustomAlert
          type={alertData.type}
          title={alertData.title}
          message={alertData.message}
          onClose={() => {
            setAlertData((prev) => ({
              ...prev,
              show: false,
            }));

            if (alertData.type === "success") {
              dispatch(clearAuthMessages());

              navigate("/business-profile");
            }
          }}
        />
      )}

      <div className={signupstyles.buyerSignupPage}>
        <div className={signupstyles.buyerSignupContent}>
          <h1>
            {role === "seller"
              ? "Sign Up as Seller"
              : role === "transporter"
                ? "Sign Up as Transporter"
                : "Sign Up"}
          </h1>
          <p>
            {role === "seller"
              ? "Create your seller account to continue"
              : role === "transporter"
                ? "Create your transporter account to continue"
                : "Create your account to continue"}
          </p>

          <form
            className={signupstyles.buyerSignupForm}
            onSubmit={handleSubmit}
          >
            <div className={signupstyles.inputGroup}>
              <label>Full Name</label>
              <input
                type="text"
                name="fullName"
                placeholder="Ravi Kumar..."
                value={formData.fullName}
                onChange={handleChange}
              />
            </div>

            <div className={signupstyles.inputGroup}>
              <label>Email</label>
              <input
                type="email"
                name="email"
                placeholder="newuser@gmail.com"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className={signupstyles.inputGroup}>
              <label>Location (optional)</label>
              <input
                type="text"
                name="location"
                placeholder="e.g., Hyderabad, Telangana"
                value={formData.location}
                onChange={handleChange}
              />
            </div>

            <div className={signupstyles.inputGroup}>
              <label>Password</label>

              <div className={signupstyles.passwordWrapper}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                />

                <span
                  className={signupstyles.eyeIcon}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </div>
            </div>

            <div className={signupstyles.inputGroup}>
              <label>Confirm Password</label>

              <div className={signupstyles.passwordWrapper}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />

                <span
                  className={signupstyles.eyeIcon}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </div>
            </div>

            <button type="submit" className={signupstyles.signupBtn}>
              {signupLoading
                ? `Signing Up as ${role.charAt(0).toUpperCase() + role.slice(1)}...`
                : role === "seller"
                  ? "Sign Up as Seller"
                  : role === "transporter"
                    ? "Sign Up as Transporter"
                    : "Sign Up"}
            </button>

            <div className={signupstyles.divider}>
              <span>OR</span>
            </div>

            <button
              type="button"
              className={signupstyles.googleBtn}
              onClick={() => handleGoogleSignup()}
            >
              <FcGoogle className={signupstyles.googleIcon} />
              {role === "seller"
                ? "Sign Up as Seller with Google"
                : "Sign Up with Google"}
            </button>

            <p className={signupstyles.loginText}>
              Already have an account?{" "}
              <span onClick={() => navigate("/login")}>Login here</span>
            </p>

            <div
              style={{
                display: "flex",
                gap: "10px",
                flexDirection: "column",
              }}
            >
              <button
                type="button"
                className={signupstyles.sellerBtn}
                onClick={() => setRole("buyer")}
              >
                Continue as Buyer
              </button>

              <button
                type="button"
                className={signupstyles.sellerBtn}
                onClick={() => setRole("seller")}
              >
                Sign Up as Seller
              </button>

              <button
                type="button"
                className={signupstyles.sellerBtn}
                onClick={() => setRole("transporter")}
              >
                Sign Up as Transporter
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default Signup;
